import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHeartCorner } from "../../hooks/useHeartCorner";
import { useCurrentUser } from "../../../hooks/useGetUserDetail";
import LottieFromUrl from "../../components/LottieFromUrl/LottieFromUrl";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import ChatBubble from "../Rooms/components/ChatBubble/ChatBubble";
import { journeySnowfloor } from "../../appAssets";
import { webRoutes } from "../../router/webRoutes";
import {
  HeartBlock,
  HeartEmoji,
  HeartScreen,
  HeartTile,
  convertStringToID,
} from "./types";
import "./HeartCorner.scss";
import closeIcon from "../../appAssets/closeIcon.png";
import YetiCharacter from "../Rooms/components/YetiCharacter/YetiCharacter";
import { TrackingEvents } from "../../Utilities/analytics";
import { trackEvent } from "../../../mixpanel";

interface HeartCornerProps {
  /** Initial heart block to display (mobile: conversationValue) */
  conversationValue: HeartBlock | null;
  /** All heart + emotion blocks for emotion-linked lookups (mobile: allBlocksData) */
  allBlocksData: { blocks: HeartBlock[] } | null;
  /** Fires after emoji pick — passes the selected emotion string (mobile: updateApiCall) */
  updateApiCall?: (emotion: string) => void;
  /** Optional close (only used when HeartCorner is rendered without parent ownership) */
  onClose?: () => void;
}

/**
 * Mirrors srcMobile/Screens/HeartCorner/index.tsx
 * Rendered INLINE inside Rooms (not a route) when renderHeartModule is true.
 *
 * Three internal screens:
 *  1. HeartCorner  — Yeti + auto-advancing chat + emoji picker
 *  2. Feeling      — Linked emotion block intro + Continue
 *  3. WriteAboutIt — "When you are <feeling>" + tiles → activity screen
 */
function HeartCorner({
  conversationValue,
  allBlocksData,
  updateApiCall,
  onClose,
}: HeartCornerProps) {
  const navigate = useNavigate();
  const { data: userData } = useCurrentUser();
  const { postHeartCorner } = useHeartCorner();

  // ── Local state
  const [currentScreen, setCurrentScreen] =
    useState<HeartScreen>("HeartCorner");
  const [conversationData, setConversationData] = useState<HeartBlock | null>(
    conversationValue,
  );
  const [conversationIndex, setConversationIndex] = useState(0);
  const [objectIndex, setObjectIndex] = useState(0);
  const [emojiLink, setEmojiLink] = useState<number | null>(null);
  const [feeling, setFeeling] = useState("");
  const [selectedTile, setSelectedTile] = useState<HeartTile | null>(null);
  const [showYeti, setShowYeti] = useState(true);
  const [emojiList, setEmojiList] = useState<HeartEmoji[]>([]);

  const userComplexity = (userData?.complexity ?? "Easy").toLowerCase();

  // Mobile resets conversationData when conversationValue prop changes
  useEffect(() => {
    if (conversationValue) {
      setConversationData(conversationValue);
      setConversationIndex(0);
      setObjectIndex(0);
    }
  }, [conversationValue]);

  // Mobile: once we hit the last conversation, surface the emoji picker
  useEffect(() => {
    if (
      currentScreen !== "WriteAboutIt" &&
      conversationData &&
      conversationIndex === (conversationData.conversations?.length ?? 0) - 1
    ) {
      const emojis = conversationData.userActions?.[0]?.emojis ?? [];
      setEmojiList(
        emojis.filter((e) =>
          e.level?.some((lv) => lv.toLowerCase() === userComplexity),
        ),
      );
    }
  }, [currentScreen, conversationIndex, conversationData, userComplexity]);

  // ── Auto-advance conversation (mirrors mobile timer logic)
  useEffect(() => {
    if (!conversationData) return;
    const conversations = conversationData.conversations ?? [];
    const currentConversation = conversations[conversationIndex]?.object ?? [];
    if (currentConversation.length === 0) return;

    const lastConversationIdx = conversations.length - 1;
    const lastObjectIdx = currentConversation.length - 1;

    // Past the last object of the last conversation? Hold the screen.
    if (
      conversationIndex === lastConversationIdx &&
      objectIndex >= lastObjectIdx
    ) {
      return;
    }

    const duration = parseInt(
      currentConversation[objectIndex]?.duration ?? "",
      10,
    );
    const delay = Number.isFinite(duration) ? duration + 2000 : 5000;

    const timer = setTimeout(() => {
      setShowYeti((v) => !v);
      if (objectIndex < currentConversation.length - 1) {
        setObjectIndex((i) => i + 1);
      } else if (conversationIndex < lastConversationIdx) {
        setObjectIndex(0);
        setConversationIndex((i) => i + 1);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [conversationData, conversationIndex, objectIndex]);

  // ── Done button — POST emotion + advance to "Feeling" screen
  const handleDonePress = async () => {
    if (emojiLink == null || !conversationData || !allBlocksData) return;
    const linkedBlock = allBlocksData.blocks.find((b) => b.id === emojiLink);
    console.log(linkedBlock, "linkedBlock");

    if (!linkedBlock) return;

    await postHeartCorner({
      heartConvoType: (conversationData.heartBlockType ?? "").toLowerCase(),
      emotionType: feeling.toLowerCase(),
    });

    // updateApiCall?.(feeling);

    setConversationData(linkedBlock);
    setConversationIndex(0);
    setObjectIndex(0);
    setCurrentScreen("Feeling");
  };

  // ── Tile click navigation (mirrors mobile handleWriteButton)
  const handleWriteButton = () => {
    if (!selectedTile) return;
    const link = selectedTile.linkTo.toLowerCase();
    console.log(link, "linklinklinklinklinklink");
    if (link === "journalscreen" || link === "kidsjournal")
      return navigate(`${webRoutes.journal}?navigatedFrom=heartcorner`);
    if (link === "adventurescreen") return navigate(webRoutes.journey);
    if (link === "storyscreen") return navigate(webRoutes.chatbox);
    if (link === "letsdance") return navigate(webRoutes.breatheDance);
    if (link === "calmmusic") return navigate(webRoutes.breatheMusic);
    if (link === "calmcorner") return navigate(webRoutes.breathe);
    if (link === "grownup") return navigate(webRoutes.grownup);
    navigate(webRoutes.rooms);
  };

  // ── Active conversation slice
  const currentConversation =
    conversationData?.conversations?.[conversationIndex]?.object ?? [];
  const currentObject = currentConversation[objectIndex] ?? {};
  const isSingleObject = currentConversation.length === 1;

  // Feeling/WriteAboutIt use the first conversation of the linked block
  const feelingObject = conversationData?.conversations?.[0]?.object?.[0];
  const tiles =
    conversationData?.userActions?.find(
      (a) => a.component === "block.tiles-input",
    )?.tiles ?? [];

  return (
    <div className="heart">
      <img src={journeySnowfloor} alt="" className="heart__bg" />

      {/* Close button (only when standalone — Rooms-wrapped variant omits this) */}
      {onClose && (
        <button
          type="button"
          className="heart__close"
          onClick={onClose}
          aria-label="Close"
        >
          <img src={closeIcon} alt="Close" className="heart__close-icon" />
        </button>
      )}

      {/* ── Screen 1: HeartCorner ── */}
      {currentScreen === "HeartCorner" && conversationData && (
        <div className="heart__content">
          <div className="heart__yeti-row">
            {/* Always render the first object's character (Troo) — never swaps when objectIndex advances */}
            <YetiCharacter
              characterImage={
                currentConversation[0]?.characterImage ??
                currentObject?.characterImage
              }
              className="heart__yeti"
            />
            {currentObject.content?.trim() && (
              <div className="heart__bubble">
                <ChatBubble
                  key={`${conversationIndex}-${objectIndex}`}
                  content={currentObject.content}
                  textColor={currentObject.textColor}
                  audioUri={currentObject.audio}
                  tailSide={isSingleObject || showYeti ? "left" : "right"}
                />
              </div>
            )}
          </div>

          {emojiList.length > 0 && (
            <div className="heart__emoji-section">
              <div className="heart__emoji-row">
                {emojiList.map((emoji) => {
                  const id = convertStringToID(emoji.linkTo);
                  const isActive = emojiLink === id;
                  return (
                    <button
                      key={emoji.linkTo}
                      type="button"
                      className={`heart__emoji${isActive ? " heart__emoji--active" : ""}`}
                      onClick={() => {
                        trackEvent(TrackingEvents.heart_corner_feeling, {
                          emoji_selected: emoji.emojiText,
                        });
                        setEmojiLink(id);
                        setFeeling(emoji.emojiText);
                      }}
                    >
                      <LottieFromUrl
                        url={emoji.image}
                        className="heart__emoji-lottie"
                      />

                      <span className="heart__emoji-label">
                        {emoji.emojiText}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className={`heart__continue${emojiLink == null ? " heart__continue--disabled" : ""}`}
                disabled={emojiLink == null}
                onClick={handleDonePress}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Screen 2: Feeling ── */}
      {currentScreen === "Feeling" && feelingObject && (
        <div className="heart__feeling">
          <p className="heart__feeling-text">{feelingObject.content}</p>
          <LottieFromUrl
            url={feelingObject.characterImage}
            className="heart__yeti"
          />
          <button
            type="button"
            className="heart__continue"
            onClick={() => setCurrentScreen("WriteAboutIt")}
          >
            Continue
          </button>
          {feelingObject.audio && (
            <div className="heart__feeling-audio">
              <AudioPlayer
                uri={feelingObject.audio}
                autoPlay
                variant="compact"
              />
            </div>
          )}
        </div>
      )}

      {/* ── Screen 3: WriteAboutIt ── */}
      {currentScreen === "WriteAboutIt" && (
        <div className="heart__write">
          <LottieFromUrl
            url={feelingObject?.characterImage ?? ""}
            className="heart__write-yeti"
          />
          <div className="heart__write-card">
            <p className="heart__write-prompt">
              When you are{" "}
              <span className="heart__write-feeling">
                {feeling.toLowerCase()}
              </span>
            </p>
            <div className="heart__tiles">
              {tiles.map((tile) => {
                const active = selectedTile?.id === tile.id;
                return (
                  <button
                    key={tile.id}
                    type="button"
                    className={`heart__tile${active ? " heart__tile--active" : ""}`}
                    onClick={() => setSelectedTile(tile)}
                  >
                    <LottieFromUrl
                      url={tile.icon}
                      className="heart__tile-icon"
                    />
                    <span className="heart__tile-label">{tile.title}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`heart__continue${!selectedTile ? " heart__continue--disabled" : ""}`}
              disabled={!selectedTile}
              onClick={handleWriteButton}
            >
              Okay!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeartCorner;
